import { emailToUsername, normalizeEmail } from './emailUtils';

import { logPortalActivityQuiet } from './portalActivityLog';

import {
  adminSetPortalDisplayName,
  adminSetPortalRole,
  fetchAllPortalAccessRows,
  type PortalAccessRow,
} from './accessManagement';
import {
  addPortalAllowedEmail,
  assertCrmAllowlistRemovable,
  fetchPortalAllowedEmails,
  removePortalAllowedEmail,
  updatePortalAllowedEmail,
  type AllowlistPortalKind,
} from './portalAllowlist';
import { formatSupabaseError } from './formatSupabaseError';
import {

  softRemovePortalUserProfile,

  restorePortalUserProfile,

  fetchPortalUserProfiles,

  upsertPortalUserProfile,

  type PortalUserProfile,

} from './portalUserProfiles';

import {

  canDeleteAllowedEmail,

  canEditFormsPortalRole,

  canManageAllowedEmails,

  normalizeUserRole,

  type FormsPortalRole,

} from './settingsPermissions';

import type { AllowedEmail, UserRole } from '../types/crm';



export const NO_PORTAL_ACCESS = 'none' as const;

export type PortalAccessRole = UserRole | typeof NO_PORTAL_ACCESS;



export type PortalUserSlot = {

  id?: string;

  role: PortalAccessRole;

  display_name?: string | null;

  department_id?: string | null;

};



export type UnifiedPortalUser = {

  email: string;

  displayName: string;

  /** Shared across all portals — required for every person. */
  departmentId: string | null;

  /** NAV salesperson codes — limits Accounts Collection to assigned customers when set. */
  salespersonCodes: string[];

  /** Soft-removed users stay listed under Removed users; CRM data is kept. */
  removedAt: string | null;

  removedBy: string | null;

  removedReason: string | null;

  crm: PortalUserSlot;

  it: PortalUserSlot;

  dem: PortalUserSlot;

  forms: PortalUserSlot;

  report: PortalUserSlot;

  accounts: PortalUserSlot;

  purchasing: PortalUserSlot;

  design: PortalUserSlot;

};



const REMOVAL_REASON = 'Access removed in Settings user management.';



function slotFromRow(row: AllowedEmail | undefined): PortalUserSlot {

  if (!row) return { role: NO_PORTAL_ACCESS };

  const raw = String(row.role ?? '').trim().toLowerCase();

  const role: PortalAccessRole =

    !raw || raw === NO_PORTAL_ACCESS ? NO_PORTAL_ACCESS : normalizeUserRole(raw);

  return {

    id: row.id,

    role,

    display_name: row.display_name,

    department_id: row.department_id ?? null,

  };

}

function slotFromWideRole(
  roleRaw: string | null | undefined,
  displayName: string | null | undefined,
  departmentId?: string | null,
): PortalUserSlot {
  const raw = String(roleRaw ?? '').trim().toLowerCase();
  if (!raw || raw === NO_PORTAL_ACCESS) return { role: NO_PORTAL_ACCESS };
  return {
    role: normalizeUserRole(raw),
    display_name: displayName ?? null,
    department_id: departmentId ?? null,
  };
}

/** Build Admin Hub rows from access_management.portal_access (+ profiles for salesperson). */
export function mergeFromPortalAccess(
  wideRows: PortalAccessRow[],
  profiles: PortalUserProfile[] = [],
): UnifiedPortalUser[] {
  const byEmail = new Map<string, UnifiedPortalUser>();

  for (const wide of wideRows) {
    const key = normalizeEmail(wide.email);
    const display =
      wide.display_name?.trim() || emailToUsername(key);
    byEmail.set(key, {
      email: key,
      displayName: display,
      departmentId: wide.department_id ?? null,
      salespersonCodes: [],
      removedAt: wide.removed_at ?? null,
      removedBy: wide.removed_by ?? null,
      removedReason: wide.removed_reason ?? null,
      crm: slotFromWideRole(wide.crm_role, wide.display_name),
      it: slotFromWideRole(wide.it_role, wide.display_name),
      dem: slotFromWideRole(wide.dem_role, wide.display_name),
      forms: slotFromWideRole(wide.forms_role, wide.display_name, wide.department_id),
      report: slotFromWideRole(wide.report_role, wide.display_name),
      accounts: slotFromWideRole(wide.accounts_role, wide.display_name),
      purchasing: slotFromWideRole(wide.purchasing_role, wide.display_name),
      design: slotFromWideRole(wide.design_role, wide.display_name),
    });
  }

  for (const profile of profiles) {
    const key = normalizeEmail(profile.email);
    let user = byEmail.get(key);
    if (!user) {
      user = {
        email: key,
        displayName: profile.display_name?.trim() || emailToUsername(key),
        departmentId: profile.department_id ?? null,
        salespersonCodes: [],
        removedAt: profile.removed_at ?? null,
        removedBy: profile.removed_by ?? null,
        removedReason: profile.removed_reason ?? null,
        crm: { role: NO_PORTAL_ACCESS },
        it: { role: NO_PORTAL_ACCESS },
        dem: { role: NO_PORTAL_ACCESS },
        forms: { role: NO_PORTAL_ACCESS },
        report: { role: NO_PORTAL_ACCESS },
        accounts: { role: NO_PORTAL_ACCESS },
        purchasing: { role: NO_PORTAL_ACCESS },
        design: { role: NO_PORTAL_ACCESS },
      };
      byEmail.set(key, user);
    }
    if (profile.department_id) user.departmentId = profile.department_id;
    const codes = (profile.salesperson_codes ?? []).map((c) => c.trim()).filter(Boolean);
    if (codes.length > 0) user.salespersonCodes = codes;
    const name = profile.display_name?.trim();
    if (name) user.displayName = name;
    if (profile.removed_at) {
      user.removedAt = profile.removed_at;
      user.removedBy = profile.removed_by ?? null;
      user.removedReason = profile.removed_reason ?? null;
    }
  }

  return [...byEmail.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }),
  );
}

async function resolveAllowlistId(
  kind: AllowlistPortalKind,
  email: string,
  existingId?: string,
): Promise<string | undefined> {
  if (existingId) return existingId;
  const rows = await fetchPortalAllowedEmails(kind);
  const key = normalizeEmail(email);
  return rows.find((row) => normalizeEmail(row.email) === key)?.id;
}



export function mergePortalAllowlists(

  crmRows: AllowedEmail[],

  itRows: AllowedEmail[],

  demRows: AllowedEmail[],

  formsRows: AllowedEmail[],

  reportRows: AllowedEmail[],

  accountsRows: AllowedEmail[],

  purchasingRows: AllowedEmail[],

  designRows: AllowedEmail[],

  profiles: PortalUserProfile[] = [],

): UnifiedPortalUser[] {

  const byEmail = new Map<string, UnifiedPortalUser>();



  const ensure = (email: string): UnifiedPortalUser => {

    const key = normalizeEmail(email);

    let row = byEmail.get(key);

    if (!row) {

      row = {

        email: key,

        displayName: '',

        departmentId: null,

        salespersonCodes: [],

        removedAt: null,

        removedBy: null,

        removedReason: null,

        crm: { role: NO_PORTAL_ACCESS },

        it: { role: NO_PORTAL_ACCESS },

        dem: { role: NO_PORTAL_ACCESS },

        forms: { role: NO_PORTAL_ACCESS },

        report: { role: NO_PORTAL_ACCESS },

        accounts: { role: NO_PORTAL_ACCESS },

        purchasing: { role: NO_PORTAL_ACCESS },

        design: { role: NO_PORTAL_ACCESS },

      };

      byEmail.set(key, row);

    }

    return row;

  };



  const apply = (rows: AllowedEmail[], kind: AllowlistPortalKind) => {

    for (const entry of rows) {

      const user = ensure(entry.email);

      const slot = slotFromRow(entry);

      if (kind === 'crm') user.crm = slot;

      if (kind === 'it') user.it = slot;

      if (kind === 'dem') user.dem = slot;

      if (kind === 'forms') user.forms = slot;

      if (kind === 'report') user.report = slot;

      if (kind === 'accounts') user.accounts = slot;

      if (kind === 'purchasing') user.purchasing = slot;
      if (kind === 'design') user.design = slot;



      const name = entry.display_name?.trim();

      if (name && !user.displayName) {

        user.displayName = name;

      }

    }

  };



  apply(crmRows, 'crm');

  apply(itRows, 'it');

  apply(demRows, 'dem');

  apply(formsRows, 'forms');

  apply(reportRows, 'report');

  apply(accountsRows, 'accounts');

  apply(purchasingRows, 'purchasing');

  apply(designRows, 'design');



  for (const profile of profiles) {

    const user = ensure(profile.email);

    if (profile.department_id) user.departmentId = profile.department_id;

    const codes = (profile.salesperson_codes ?? [])
      .map((code) => code.trim())
      .filter(Boolean);
    if (codes.length > 0) user.salespersonCodes = codes;

    const name = profile.display_name?.trim();

    if (name && !user.displayName) user.displayName = name;

    if (profile.removed_at) {
      user.removedAt = profile.removed_at;
      user.removedBy = profile.removed_by ?? null;
      user.removedReason = profile.removed_reason ?? null;
    }

  }



  // Fallback: Forms slot department if profile missing.

  for (const user of byEmail.values()) {

    if (!user.departmentId && user.forms.department_id) {

      user.departmentId = user.forms.department_id;

    }

  }



  for (const user of byEmail.values()) {

    if (!user.displayName) {

      user.displayName =

        user.crm.display_name?.trim() ||

        user.it.display_name?.trim() ||

        user.dem.display_name?.trim() ||

        user.forms.display_name?.trim() ||

        user.report.display_name?.trim() ||

        user.accounts.display_name?.trim() ||

        user.purchasing.display_name?.trim() ||

        user.design.display_name?.trim() ||

        emailToUsername(user.email);

    }

  }



  return [...byEmail.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }),
  );
}

export function findUnifiedPortalUser(
  rows: UnifiedPortalUser[],
  email: string,
): UnifiedPortalUser | undefined {
  const key = normalizeEmail(email);
  return rows.find((row) => normalizeEmail(row.email) === key);
}

export function unifiedDisplayNameForEmail(
  rows: UnifiedPortalUser[],
  email: string,
): string | null {
  const row = findUnifiedPortalUser(rows, email);
  const name = row?.displayName?.trim();
  return name || null;
}



export async function fetchUnifiedPortalUsers(options?: {
  includeRemoved?: boolean;
}): Promise<UnifiedPortalUser[]> {
  const [wideRows, profiles] = await Promise.all([
    fetchAllPortalAccessRows(),
    fetchPortalUserProfiles().catch(() => [] as PortalUserProfile[]),
  ]);

  // SoT only — legacy allowlist tables/views are gone.
  const merged = mergeFromPortalAccess(wideRows, profiles);

  if (options?.includeRemoved) return merged;
  return merged.filter((user) => !user.removedAt);
}



export async function setUnifiedPortalUserRole(

  kind: AllowlistPortalKind,

  email: string,

  displayName: string,

  nextRole: PortalAccessRole,

  existingId?: string,

  formsDepartmentId?: string | null,

): Promise<void> {

  const normalized = normalizeEmail(email);
  const allowlistId = await resolveAllowlistId(kind, normalized, existingId);

  // Phase 3: prefer wide-table SoT (mirrors legacy in SQL).
  if (nextRole === NO_PORTAL_ACCESS) {
    if (kind === 'crm' && allowlistId) {
      await assertCrmAllowlistRemovable(allowlistId);
    }
    const wide = await adminSetPortalRole({
      email: normalized,
      portal: kind,
      role: null,
      reason: REMOVAL_REASON,
    });
    if (wide === 'rpc_missing') {
      if (!allowlistId) return;
      await removePortalAllowedEmail(kind, allowlistId, REMOVAL_REASON, {
        skipOwnershipCheck: true,
      });
    }
    logPortalActivityQuiet({
      portal: 'admin',
      action: 'delete',
      entityType: 'allowlist',
      entityId: allowlistId,
      title: `Access removed (${kind.toUpperCase()}): ${normalized}`,
      detail: REMOVAL_REASON,
      metadata: { portal_kind: kind, email: normalized },
    });
    return;
  }

  if (kind === 'forms' && !formsDepartmentId && !allowlistId) {
    throw new Error('Department is required before granting Application Form access.');
  }

  const wide = await adminSetPortalRole({
    email: normalized,
    portal: kind,
    role: nextRole,
    displayName,
    departmentId: formsDepartmentId,
  });
  if (wide === 'ok') {
    logPortalActivityQuiet({
      portal: 'admin',
      action: allowlistId ? 'update' : 'create',
      entityType: 'allowlist',
      entityId: allowlistId,
      title: allowlistId
        ? `Access updated (${kind.toUpperCase()}): ${normalized}`
        : `Access granted (${kind.toUpperCase()}): ${normalized}`,
      detail: `Role: ${nextRole}. Display name: ${displayName.trim() || emailToUsername(normalized)}.`,
      metadata: { portal_kind: kind, email: normalized, role: nextRole },
    });
    return;
  }

  if (!allowlistId) {
    try {
      await addPortalAllowedEmail(kind, {
        email: normalized,
        displayName,
        role: nextRole,
        departmentId: formsDepartmentId,
      });
    } catch (error) {
      throw new Error(formatSupabaseError(error));
    }
    logPortalActivityQuiet({
      portal: 'admin',
      action: 'create',
      entityType: 'allowlist',
      title: `Access granted (${kind.toUpperCase()}): ${normalized}`,
      detail: `Role: ${nextRole}. Display name: ${displayName.trim() || emailToUsername(normalized)}.`,
      metadata: { portal_kind: kind, email: normalized, role: nextRole },
    });
    return;
  }

  await updatePortalAllowedEmail(kind, allowlistId, {
    role: nextRole,
    display_name: displayName.trim() || null,
    ...(kind === 'forms' && formsDepartmentId ? { department_id: formsDepartmentId } : {}),
  });
  logPortalActivityQuiet({
    portal: 'admin',
    action: 'update',
    entityType: 'allowlist',
    entityId: allowlistId,
    title: `Access updated (${kind.toUpperCase()}): ${normalized}`,
    detail: `Role set to ${nextRole}.`,
    metadata: { portal_kind: kind, email: normalized, role: nextRole },
  });
}

export async function updateUnifiedPortalUserDisplayName(

  user: UnifiedPortalUser,

  displayName: string,

): Promise<void> {

  const trimmed = displayName.trim();

  const wide = await adminSetPortalDisplayName(user.email, trimmed);
  if (wide === 'ok') {
    logPortalActivityQuiet({
      portal: 'admin',
      action: 'update',
      entityType: 'allowlist',
      title: `Display name updated: ${user.email}`,
      detail: `New display name: ${trimmed || emailToUsername(user.email)}.`,
      metadata: { email: user.email },
    });
    return;
  }

  const patch = async (kind: AllowlistPortalKind, slot: PortalUserSlot) => {
    if (slot.role === NO_PORTAL_ACCESS) return;
    const id = await resolveAllowlistId(kind, user.email, slot.id);
    if (!id) return;
    await updatePortalAllowedEmail(kind, id, { display_name: trimmed || null });
  };

  await Promise.all([
    patch('crm', user.crm),
    patch('it', user.it),
    patch('dem', user.dem),
    patch('forms', user.forms),
    patch('report', user.report),
    patch('accounts', user.accounts),
    patch('purchasing', user.purchasing),
    patch('design', user.design),
  ]);

  logPortalActivityQuiet({

    portal: 'admin',

    action: 'update',

    entityType: 'allowlist',

    title: `Display name updated: ${user.email}`,

    detail: `New display name: ${trimmed || emailToUsername(user.email)}.`,

    metadata: { email: user.email },

  });

}





export async function setUnifiedPortalUserDepartment(
  email: string,
  departmentId: string,
  displayName?: string | null,
  _formsAllowlistId?: string | null,
): Promise<void> {
  if (!departmentId.trim()) {
    throw new Error('Department is required.');
  }

  const normalized = normalizeEmail(email);

  // Profile only. Forms allowlist department sync uses update_allowed_email, which
  // currently calls the ambiguous 3-arg upsert_portal_user_profile overload —
  // do not call it until migration 223 drops that overload.
  await upsertPortalUserProfile({
    email: normalized,
    displayName: displayName ?? null,
    departmentId,
  });

  logPortalActivityQuiet({
    portal: 'admin',
    action: 'update',
    entityType: 'allowlist',
    title: `Department updated: ${normalized}`,
    detail: `Department id: ${departmentId}.`,
    metadata: { email: normalized, department_id: departmentId },
  });
}

export async function setUnifiedPortalUserSalespersonCodes(
  email: string,
  salespersonCodes: string[],
  displayName?: string | null,
  departmentId?: string | null,
): Promise<void> {
  const normalized = normalizeEmail(email);
  const dept = departmentId?.trim();
  if (!dept) {
    throw new Error('Department is required before setting salesperson codes.');
  }
  const codes = [...new Set(salespersonCodes.map((c) => c.trim()).filter(Boolean))];
  await upsertPortalUserProfile({
    email: normalized,
    displayName: displayName ?? null,
    departmentId: dept,
    salespersonCodes: codes,
  });

  // Keep AF master list in sync when assigning codes (notification recipients come from profiles).
  const { upsertSalespersonEmail, fetchSalespersonEmails } = await import('./applicationFormSettings');
  const existing = await fetchSalespersonEmails().catch(() => []);
  const byCode = new Map(existing.map((row) => [row.code.trim(), row]));
  await Promise.all(
    codes.map(async (code) => {
      const prev = byCode.get(code);
      await upsertSalespersonEmail({
        code,
        sort_order: prev?.sort_order ?? 0,
      });
    }),
  );

  logPortalActivityQuiet({
    portal: 'admin',
    action: 'update',
    entityType: 'allowlist',
    title: `Salesperson codes updated: ${normalized}`,
    detail: codes.length > 0 ? `Salesperson: ${codes.join(', ')}` : 'Salesperson codes cleared.',
    metadata: { email: normalized, salesperson_codes: codes },
  });
}

export async function createUnifiedPortalUser(input: {

  email: string;

  displayName: string;

  crm: PortalAccessRole;

  it: PortalAccessRole;

  dem: PortalAccessRole;

  forms: PortalAccessRole;

  report: PortalAccessRole;

  accounts: PortalAccessRole;

  purchasing: PortalAccessRole;

  design: PortalAccessRole;

  departmentId: string;

  salespersonCodes?: string[];

  /** IT/Management department — profile only; all portals granted by department. */
  elevatedByDepartment?: boolean;

}): Promise<void> {

  const email = normalizeEmail(input.email);

  const displayName = input.displayName.trim() || emailToUsername(email);

  if (!input.departmentId?.trim()) {

    throw new Error('Department is required.');

  }

  await upsertPortalUserProfile({

    email,

    displayName,

    departmentId: input.departmentId,

    salespersonCodes: input.salespersonCodes ?? [],

  });

  const initialCodes = [...new Set((input.salespersonCodes ?? []).map((c) => c.trim()).filter(Boolean))];
  if (initialCodes.length > 0) {
    await setUnifiedPortalUserSalespersonCodes(
      email,
      initialCodes,
      displayName,
      input.departmentId,
    );
  }



  const writes: Promise<void>[] = [];

  if (input.crm !== NO_PORTAL_ACCESS) {

    writes.push(setUnifiedPortalUserRole('crm', email, displayName, input.crm));

  }

  if (input.it !== NO_PORTAL_ACCESS) {

    writes.push(setUnifiedPortalUserRole('it', email, displayName, input.it));

  }

  if (input.dem !== NO_PORTAL_ACCESS) {

    writes.push(setUnifiedPortalUserRole('dem', email, displayName, input.dem));

  }

  if (input.forms !== NO_PORTAL_ACCESS) {

    writes.push(

      setUnifiedPortalUserRole(

        'forms',

        email,

        displayName,

        input.forms,

        undefined,

        input.departmentId,

      ),

    );

  }



  if (input.report !== NO_PORTAL_ACCESS) {

    writes.push(setUnifiedPortalUserRole('report', email, displayName, input.report));

  }

  if (input.accounts !== NO_PORTAL_ACCESS) {

    writes.push(setUnifiedPortalUserRole('accounts', email, displayName, input.accounts));

  }

  if (input.design !== NO_PORTAL_ACCESS) {
    writes.push(setUnifiedPortalUserRole('design', email, displayName, input.design));
  }

  if (input.purchasing !== NO_PORTAL_ACCESS) {

    writes.push(setUnifiedPortalUserRole('purchasing', email, displayName, input.purchasing));

  }



  if (writes.length === 0 && !input.elevatedByDepartment) {

    throw new Error('Grant access in at least one system.');

  }



  await Promise.all(writes);

}



function canRemoveFormsSlot(actorRole: UserRole, slot: PortalUserSlot): boolean {

  if (slot.role === NO_PORTAL_ACCESS) return true;

  return canEditFormsPortalRole(actorRole, slot.role as FormsPortalRole);

}



export function canRemoveUnifiedPortalUser(

  actorRole: UserRole,

  user: UnifiedPortalUser,

): boolean {

  if (user.removedAt) return false;

  const slots = [user.crm, user.it, user.dem, user.forms, user.report, user.accounts, user.purchasing, user.design].filter(

    (slot) => slot.role !== NO_PORTAL_ACCESS,

  );

  // Profile-only users (department / salesperson, all modules No Access) must still be removable.
  if (slots.length === 0) return canManageAllowedEmails(actorRole);



  const crmItDemReportOk = [user.crm, user.it, user.dem, user.report, user.accounts, user.purchasing, user.design]

    .filter((slot) => slot.role !== NO_PORTAL_ACCESS)

    .every((slot) =>

      canDeleteAllowedEmail(actorRole, normalizeUserRole(slot.role as UserRole)),

    );



  const formsOk =

    user.forms.role === NO_PORTAL_ACCESS || canRemoveFormsSlot(actorRole, user.forms);



  return crmItDemReportOk && formsOk;

}

export function canRestoreUnifiedPortalUser(
  actorRole: UserRole,
  user: UnifiedPortalUser,
): boolean {
  return Boolean(user.removedAt) && canManageAllowedEmails(actorRole);
}



export async function removeUnifiedPortalUser(

  user: UnifiedPortalUser,

  reason: string,

): Promise<void> {

  const trimmedReason = reason.trim();

  if (!trimmedReason) {

    throw new Error('A reason is required when removing a user.');

  }



  // Soft-remove: revoke portal access, archive profile. Do NOT delete CRM accounts/contacts.
  const skipOwnership = { skipOwnershipCheck: true as const };

  const removals: Promise<void>[] = [];

  if (user.crm.id && user.crm.role !== NO_PORTAL_ACCESS) {

    removals.push(removePortalAllowedEmail('crm', user.crm.id, trimmedReason, skipOwnership));

  }

  if (user.it.id && user.it.role !== NO_PORTAL_ACCESS) {

    removals.push(removePortalAllowedEmail('it', user.it.id, trimmedReason));

  }

  if (user.dem.id && user.dem.role !== NO_PORTAL_ACCESS) {

    removals.push(removePortalAllowedEmail('dem', user.dem.id, trimmedReason));

  }

  if (user.forms.id && user.forms.role !== NO_PORTAL_ACCESS) {

    removals.push(removePortalAllowedEmail('forms', user.forms.id, trimmedReason));

  }



  if (user.report.id && user.report.role !== NO_PORTAL_ACCESS) {

    removals.push(removePortalAllowedEmail('report', user.report.id, trimmedReason));

  }

  if (user.accounts.id && user.accounts.role !== NO_PORTAL_ACCESS) {

    removals.push(removePortalAllowedEmail('accounts', user.accounts.id, trimmedReason));

  }

  if (user.design.id && user.design.role !== NO_PORTAL_ACCESS) {
    removals.push(removePortalAllowedEmail('design', user.design.id, trimmedReason));
  }

    if (user.purchasing.id && user.purchasing.role !== NO_PORTAL_ACCESS) {

    removals.push(removePortalAllowedEmail('purchasing', user.purchasing.id, trimmedReason));

  }



  removals.push(softRemovePortalUserProfile(user.email, trimmedReason));



  await Promise.all(removals);

  logPortalActivityQuiet({

    portal: 'admin',

    action: 'delete',

    entityType: 'allowlist',

    title: `User moved to Removed users: ${user.email}`,

    detail: trimmedReason,

    metadata: { email: user.email, soft_remove: true },

  });

}



export async function restoreUnifiedPortalUser(user: UnifiedPortalUser): Promise<void> {

  await restorePortalUserProfile(user.email);

  logPortalActivityQuiet({

    portal: 'admin',

    action: 'update',

    entityType: 'allowlist',

    title: `User restored: ${user.email}`,

    metadata: { email: user.email },

  });

}

